import React, { useEffect, useState, useCallback, useRef } from "react";
import { Navbar, Nav } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Compass, CompassFill,
  Gear, GearFill,
  PersonBadge, PersonBadgeFill,
  ReceiptCutoff
} from "react-bootstrap-icons";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from './SearchContext';
import SpendingIcon from './SpendingIcon';

const handleHapticsClick = async () => {
  await Haptics.impact({ style: ImpactStyle.Light });
  window.scrollTo(0, 0);
};

// Where the Preferences tab (and the offcanvas "Preferences" entry) points.
// Kept as a constant because the route is referenced three times here -- the
// tab target, the active-state check, and the searchless-route list -- and
// because MobileNavigation carries a standing note that IntegratedSearchNavBar
// documents the Preferences page as mounted at /settings/location rather than
// /preferences. This value matches what MobileNavigation currently navigates
// to; if the real mount point turns out to be /settings/location, change it
// here and in MobileNavigation together so both entrances agree.
const PREFERENCES_PATH = '/preferences';

// Helper to detect country from current path
const getCountryFromPath = (pathname) => {
  if (pathname.startsWith('/us')) return 'us';
  if (pathname.startsWith('/canada')) return 'ca';
  if (pathname.startsWith('/mexico')) return 'mx';
  if (pathname.startsWith('/uk')) return 'uk';
  if (pathname.startsWith('/australia')) return 'australia';
  if (pathname.startsWith('/france')) return 'france';
  if (pathname.startsWith('/italy')) return 'italy';
  if (pathname.startsWith('/japan')) return 'japan';
  if (pathname.startsWith('/south-africa')) return 'south-africa';
  if (pathname.startsWith('/ukraine')) return 'ukraine';
  return null;
};

// Helper to get home path for a country code
const getHomePathForCountry = (countryCode) => {
  const routes = {
    us: "/us",
    ca: "/canada",
    mx: "/mexico",
    uk: "/uk",
    australia: "/australia",
    france: "/france",
    italy: "/italy",
    japan: "/japan",
    "south-africa": "/south-africa",
    ukraine: "/ukraine"
  };
  return routes[countryCode] || "/us";
};

// Helper to get effective country (for neutral pages like /collection, /notifications)
const getEffectiveCountry = (pathname) => {
  // First check if we're on a country-specific page
  const pathCountry = getCountryFromPath(pathname);
  if (pathCountry) {
    return pathCountry;
  }

  // For neutral pages, check sessionStorage for last visited country
  const lastVisitedCountry = sessionStorage.getItem('lastVisitedCountry');
  if (lastVisitedCountry) {
    return lastVisitedCountry;
  }

  // Fall back to localStorage homeCountry (from onboarding)
  const homeCountry = localStorage.getItem('homeCountry');
  if (homeCountry) {
    return homeCountry;
  }

  // Default to US
  return 'us';
};

// Country slug -> search schema (mirrors SearchNavBar so the /search route resolves the same way)
const getSearchSchema = (countrySlug) => {
  if (countrySlug === 'all') return 'all_countries';
  const schemaMap = {
    us: 'united_states_of_america', ca: 'canada', canada: 'canada',
    mx: 'mexico', mexico: 'mexico', uk: 'united_kingdom',
    australia: 'australia', china: 'china', france: 'france',
    germany: 'germany', israel: 'israel', italy: 'italy',
    japan: 'japan', nigeria: 'nigeria', 'south-africa': 'south_africa', ukraine: 'ukraine',
  };
  return schemaMap[countrySlug] || countrySlug;
};

// Turn a feed table slug (e.g. "executive_orders") into a label ("Executive Orders").
// Mirrors FullFeedPage's formatTableName so the placeholder matches the page title.
const formatFeedName = (table) =>
  (table || '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// Routes that should never show the bottom search bar (no searchable content
// on the page). Exact-match against the current pathname.
// /fact-check and /social both stay listed even though neither is in the bottom
// nav any more -- both routes are still reachable from the offcanvas menu.
// Preferences is here for the opposite reason: it IS a tab now, and it's a
// settings page with nothing to search. The Elections and Spending tabs' pages
// are here too: each has its own controls (state and county pickers, the
// company search) and nothing the site-wide search would narrow. Add
// '/representatives' here if that page owns its own lookup input.
const SEARCHLESS_ROUTES = [
  '/fact-check',
  '/social',
  PREFERENCES_PATH,
  '/us/elections-dashboard',
  '/us/spending-dashboard',
  '/us/spending-dashboard/awards',
];

// A single member's page is mounted at /us/representative/:bioGuideId --
// singular, and NOT nested under /us/representatives -- so prefix-matching the
// tab's own path can't reach it. The Representatives tab matches this pattern
// as well, so it stays lit while a member is open. Matched by segment rather
// than the literal /us path so a future /canada/representative/:id is covered.
const MEMBER_DETAIL_RE = /^\/[^/]+\/representative\/[^/]+\/?$/i;

// A company's page sits at /us/company/:orgId, outside /us/spending, and is
// only reached from the spending dashboard's companies, awards and trades. The
// Spending tab claims it so the tab stays lit while a company is open, the way
// Representatives claims a member's page.
const COMPANY_DETAIL_RE = /^\/[^/]+\/company\/[^/]+\/?$/i;

const BottomNavbar = ({
  searchPlaceholder = 'Search Gov Glance',
  showSearch = true,
}) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();

  // Page-registered search (via usePageSearch). When a page registers an
  // onSearch handler, the bar searches in-page through that handler instead of
  // routing to /search. placeholder lets the page label the box.
  const { placeholder: ctxPlaceholder, onSearch: ctxOnSearch } = useSearchContext() || {};

  const [localSearchQuery, setLocalSearchQuery] = useState('');

  // How far to lift the search row when the native keyboard is open. The tab
  // circles stay anchored to the bottom; only the search row rides above the
  // keyboard so the input stays visible while typing. 0 = keyboard closed.
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const searchRowRef = useRef(null);

  // Native keyboard handling. By default Capacitor resizes the WebView to fit
  // above the keyboard, which shoves anything fixed to bottom:0 (this whole
  // bar) upward. Switching resize off pins the WebView, then we lift only the
  // search row -- and only by the amount needed to clear the keyboard. The row
  // already sits some distance above the screen bottom (the nav circles + their
  // padding live below it), so we subtract that gap instead of lifting by the
  // full keyboard height (which would push the input too high).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => {});
    const showP = Keyboard.addListener('keyboardWillShow', (info) => {
      const row = searchRowRef.current;
      if (!row) { setKeyboardOffset(info.keyboardHeight); return; }
      const distanceBelow = window.innerHeight - row.getBoundingClientRect().bottom;
      const lift = Math.max(0, info.keyboardHeight - distanceBelow + 8); // 8px breathing room
      setKeyboardOffset(lift);
    });
    const hideP = Keyboard.addListener('keyboardWillHide', () => setKeyboardOffset(0));
    return () => {
      showP.then((l) => l.remove());
      hideP.then((l) => l.remove());
    };
  }, []);

  // Pages that have no searchable content hide the search bar entirely,
  // regardless of the showSearch prop. The exact paths are listed in
  // SEARCHLESS_ROUTES; a company page (/us/company/:orgId) is matched by its
  // pattern, since each company has its own path, and it has nothing the
  // site-wide search would narrow.
  const hideSearchForRoute =
    SEARCHLESS_ROUTES.includes(location.pathname) || COMPANY_DETAIL_RE.test(location.pathname);

  // A /feed/:schema/:table route searches in-place: FullFeedPage reads the
  // ?query= param off the current URL, so we just set it instead of routing
  // away to /search.
  const feedMatch = location.pathname.match(/^\/feed\/[^/]+\/([^/]+)/);
  const isFeedPage = !!feedMatch;
  const feedTable = isFeedPage ? decodeURIComponent(feedMatch[1]) : '';

  // Placeholder follows the page: a page-registered placeholder wins, then the
  // feed's name on a feed page, otherwise the default ("Search Gov Glance").
  const effectivePlaceholder = ctxPlaceholder
    ? ctxPlaceholder
    : isFeedPage
      ? `Search ${formatFeedName(feedTable)}`
      : searchPlaceholder;

  // Mirror the URL's ?query= into the box on every navigation: populate it on
  // /search results and active feed searches, and clear it when landing on any
  // page that has no query (e.g. tapping a nav icon after running a search).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('query');
    setLocalSearchQuery(q ? (isFeedPage ? decodeURIComponent(q) : q) : '');
  }, [location.pathname, location.search, isFeedPage]);

  // Track current country in sessionStorage when navigating to a country page
  useEffect(() => {
    const currentCountry = getCountryFromPath(location.pathname);
    if (currentCountry) {
      sessionStorage.setItem('lastVisitedCountry', currentCountry);
    }
  }, [location.pathname]);

  // Get the home destination - prioritize session state, fall back to onboarding preference
  const getHomeDestination = () => {
    // First check sessionStorage for last visited country (active session navigation)
    const lastVisitedCountry = sessionStorage.getItem('lastVisitedCountry');
    if (lastVisitedCountry) {
      return getHomePathForCountry(lastVisitedCountry);
    }

    // Fall back to localStorage homeCountry (from onboarding - used on initial app load)
    const homeCountry = localStorage.getItem('homeCountry');
    if (homeCountry) {
      return getHomePathForCountry(homeCountry);
    }

    // Default to US
    return "/us";
  };

  const homeDestination = getHomeDestination();

  // Get the effective country context (works for both country pages and neutral pages)
  const effectiveCountry = getEffectiveCountry(location.pathname);

  // ─── Search ───
  // Priority:
  //   1. A page registered an in-page handler (usePageSearch) -> hand off to it.
  //   2. Feed page -> dispatch the in-page feed-search event.
  //   3. Otherwise -> go to the /search/:schema results page.
  const runSearch = useCallback(() => {
    const q = localSearchQuery.trim();
    if (!q) return;
    try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}

    if (ctxOnSearch) {
      ctxOnSearch(q);
      return;
    }

    if (isFeedPage) {
      window.dispatchEvent(new CustomEvent('gg:feed-search', { detail: { query: q } }));
      return;
    }

    // If we're already on a /search route, refining the search overwrites the
    // current history entry instead of stacking a new one — so back leaves the
    // search page (returns to where the search started) rather than walking
    // back through prior queries. Only record the origin when arriving from a
    // non-search page.
    const onSearchPage = location.pathname.startsWith('/search');
    if (!onSearchPage) {
      localStorage.setItem('previousPageUrl', location.pathname);
    }
    const schema = getSearchSchema(effectiveCountry);
    navigate(`/search/${schema}?query=${encodeURIComponent(q)}&limit=10`, { replace: onSearchPage });
  }, [localSearchQuery, ctxOnSearch, isFeedPage, effectiveCountry, location.pathname, navigate]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur(); // dismiss the soft keyboard on submit
      runSearch();
    }
  }, [runSearch]);

  const handleClearSearch = useCallback(() => {
    setLocalSearchQuery('');
    // A page that owns its search clears through its own handler...
    if (ctxOnSearch) {
      ctxOnSearch('');
      return;
    }
    // ...otherwise, on a feed page, clearing resets that page's in-page search.
    if (isFeedPage) {
      window.dispatchEvent(new CustomEvent('gg:feed-search', { detail: { query: '' } }));
    }
  }, [ctxOnSearch, isFeedPage]);

  // Typing the box empty also counts as a clear: on a page that owns its search
  // (e.g. recents), emptying the input restores the original unfiltered data
  // instead of leaving a stale filtered view.
  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    // Emptying the box restores the original data. Mirror the clear (×) button's
    // priority: a page-registered handler first (e.g. recents), then feed pages.
    if (value === '') {
      if (ctxOnSearch) {
        ctxOnSearch('');
      } else if (isFeedPage) {
        window.dispatchEvent(new CustomEvent('gg:feed-search', { detail: { query: '' } }));
      }
    }
  }, [ctxOnSearch, isFeedPage]);

  // Check if user's context is US (either on US page or navigated from US)
  const isUSContext = effectiveCountry === 'us';

  // Function to check if the link is active
  const isActive = ({ key, to, alsoActiveOn }) => {
    if (key === "home") {
      // Home button is active for country home pages
      const countryHomePaths = ["/us", "/canada", "/mexico", "/uk", "/australia", "/france", "/italy", "/japan", "/south-africa", "/ukraine"];
      return countryHomePaths.includes(location.pathname);
    }
    // Every other tab stays lit on its own sub-routes, not just its exact path.
    // The trailing slash in the prefix check keeps sibling routes that merely
    // share a prefix from lighting the wrong tab.
    if (location.pathname === to || location.pathname.startsWith(`${to}/`)) {
      return true;
    }
    // A tab can also claim routes that don't sit under its own path -- see
    // MEMBER_DETAIL_RE on the Representatives tab and COMPANY_DETAIL_RE on
    // Spending.
    return alsoActiveOn ? alsoActiveOn.test(location.pathname) : false;
  };

  // Define navigation items with dynamic icon switching and labels
  const navItems = [
    {
      to: homeDestination,
      icon: (active) => active ? <CompassFill size={22} className="nav-icon-active" /> : <Compass size={22} className="nav-icon-inactive" />,
      label: t('common:explore', 'Explore'),
      key: "home"
    },
    // US-only tabs. All three are backed by US data (/us/representatives,
    // /us/elections-dashboard and /us/spending-dashboard are hardcoded US
    // routes), so they only appear when the user's context is US -- on a US
    // page, or navigated from one. Social used to sit alongside them here; it
    // now lives in the offcanvas menu only (still US-gated there).
    ...(isUSContext ? [
      {
        to: "/us/representatives",
        icon: (active) => active ? <PersonBadgeFill size={22} className="nav-icon-active" /> : <PersonBadge size={22} className="nav-icon-inactive" />,
        label: t('common:representatives', 'Representatives'),
        key: "representatives",
        alsoActiveOn: MEMBER_DETAIL_RE
      },
      // The upcoming elections panel's own page (/us/elections-dashboard);
      // /us/elections is the Federal feed's Elections topic. Sub-routes stay lit
      // through the prefix check in isActive. If an individual race ends
      // up mounted outside this path -- the way a single member sits at the
      // singular /us/representative/:bioGuideId rather than under
      // /us/representatives -- give this item an alsoActiveOn regex the way
      // Representatives does.
      {
        to: "/us/elections-dashboard",
        // No -fill variant exists for this glyph, unlike the other tabs, so
        // the active state is carried by the pill background and the icon
        // color alone. Same icon either way rather than substituting a
        // different filled shape, which would read as a different tab.
        icon: (active) => <ReceiptCutoff size={22} className={active ? "nav-icon-active" : "nav-icon-inactive"} />,
        label: t('common:elections', 'Elections'),
        key: "elections"
      },
      // The spending dashboard's own page (/us/spending-dashboard); /us/spending
      // is the Federal feed's Spending topic. Took Collection's place in the
      // bar; Collection now sits under Preferences in the offcanvas menu. Same
      // glyph as the menu's Spending topic (SpendingIcon), and like Elections it
      // has no filled variant.
      {
        to: "/us/spending-dashboard",
        icon: (active) => <SpendingIcon size={22} className={active ? "nav-icon-active" : "nav-icon-inactive"} />,
        label: t('common:spending', 'Spending'),
        key: "spending",
        alsoActiveOn: COMPANY_DETAIL_RE
      }
    ] : []),
    // Preferences is country-agnostic, so it sits outside the US-only block and
    // stays last in the bar at every width.
    {
      to: PREFERENCES_PATH,
      icon: (active) => active ? <GearFill size={22} className="nav-icon-active" /> : <Gear size={22} className="nav-icon-inactive" />,
      label: t('common:preferences', 'Preferences'),
      key: "preferences"
    }
  ];

  return (
    <>
      <div className="bottom-navbar-gradient" />
      <Navbar fixed="bottom" className="custom-bottom-navbar">
        <div className="bottom-nav-inner">
          {showSearch && !hideSearchForRoute && (
            <div
              ref={searchRowRef}
              className="bottom-search-row"
              style={{ transform: keyboardOffset ? `translateY(-${keyboardOffset}px)` : undefined }}
            >
              <input
                type="text"
                className="bottom-search-input"
                placeholder={effectivePlaceholder}
                value={localSearchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
                aria-label={effectivePlaceholder}
              />
              {localSearchQuery && (
                <button type="button" className="bottom-search-clear" onClick={handleClearSearch} aria-label={t('common:clearSearch', 'Clear search')}>×</button>
              )}
            </div>
          )}

          {/* Layout lives entirely in .nav-container below rather than in
              Bootstrap utility classes: w-100 and justify-content-around are
              both !important, which left no way for the desktop rule to center
              the row once five labeled pills outgrew the 600px band. */}
          <Nav className="nav-container">
            {navItems.map((item) => {
              const { to, icon, label, key } = item;
              const active = isActive(item);
              return (
                <Nav.Link as={Link} to={to} key={key} onClick={handleHapticsClick} className="nav-item-link text-center">
                  <div className={`nav-circle ${active ? 'nav-circle-active' : ''}`}>
                    {icon(active)}
                    <span className={`nav-label d-none d-md-inline ${active ? 'nav-label-active' : ''}`}>
                      {label}
                    </span>
                  </div>
                </Nav.Link>
              );
            })}
          </Nav>
        </div>
      </Navbar>

      <style>{`
  .bottom-navbar-gradient {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 160px;
    background: linear-gradient(to top, var(--bg-primary) 70%, transparent);
    pointer-events: none;
    z-index: 5;
  }

  .custom-bottom-navbar {
    background-color: transparent;
  }

  .bottom-nav-inner {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  /* Mobile: icon-only circles spread across the full width. */
  .nav-container {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-around;
    width: 100%;
    padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 8px) 0;
  }

  /* ─── Search row (above the nav circles, at every width) ─── */
  .bottom-search-row {
    display: flex;
    align-items: center;
    position: relative;
    padding: 0 16px;
    transition: transform 0.25s ease-in-out;
  }

  .bottom-search-input {
    flex: 1;
    height: 40px;
    padding: 0 40px 0 18px;
    font-size: 16px; /* 16px prevents iOS focus-zoom */
    border: 0;
    border-radius: 20px;
    background-color: var(--search-bg);
    color: var(--text-primary);
    outline: none;
    box-shadow: 0 2px 10px var(--shadow-color, rgba(0,0,0,0.15));
  }

  .bottom-search-input:focus {
    background-color: var(--search-focus-bg);
  }

  .bottom-search-input::placeholder {
    color: var(--text-muted);
  }

  .bottom-search-clear {
    position: absolute;
    right: 28px;
    background: none;
    border: none;
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    padding: 0;
    cursor: pointer;
  }

  .bottom-search-clear:hover {
    color: var(--text-primary);
  }

  /* ─── The tab link itself ───
     A tapped tab keeps focus after it navigates, so the tab for the page you
     are on is always the focused one -- and Bootstrap's .nav-link focus styles
     (a rectangular box-shadow ring around the whole link, plus a link color)
     painted a square box behind and around that one rounded pill. Phones add
     a grey tap-highlight square on top. That is what made the current page's
     tab look different from every other tab, most visibly on a light
     background.

     The link is pinned to a plain, transparent box in every state, so each
     tab looks the same whether or not it has focus. Keyboard focus still gets
     a ring, but on the rounded pill (see .nav-circle below) rather than as a
     square around the link. */
  .nav-item-link,
  .nav-item-link:hover,
  .nav-item-link:focus,
  .nav-item-link:focus-visible,
  .nav-item-link:active,
  .nav-item-link.active {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
  }

  .nav-item-link:focus-visible .nav-circle {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }

  .nav-circle {
    background-color: var(--navbar-pill-bg);
    border-radius: 50px;
    padding: 10px 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    white-space: nowrap;
    transition: background-color 0.3s ease-in-out;
  }

  .nav-circle-active {
    background-color: var(--navbar-pill-active-bg);
  }

  .nav-icon-active {
    color: var(--text-primary);
  }

  .nav-icon-inactive {
    color: var(--text-muted);
  }

  .nav-label {
    color: var(--text-muted);
    font-size: 14px;
    font-weight: 500;
  }

  .nav-label-active {
    color: var(--text-primary);
  }

  /* Desktop: the top bar no longer carries a search input, so the bottom search
     row stays visible here too, sitting directly above the tab pills. The search
     row is capped to a centered 600px band. The tab row is centered on the same
     axis but sized to its pills rather than capped: with labels showing, five
     tabs run wider than 600px, and a capped row overflowed to the right, which
     pushed the whole bar off center. Spacing comes from the gap rather than
     space-around, so the pills sit together as one group. */
  @media (min-width: 768px) {
    .bottom-search-row {
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
      padding: 0;
    }

    .bottom-search-clear {
      right: 12px;
    }

    .nav-container {
      justify-content: center;
      gap: 8px;
    }

    .nav-item-link {
      padding-left: 0;
      padding-right: 0;
    }
  }

  /* Light theme overrides */
  [data-theme="light"] .nav-circle {
    background-color: var(--navbar-pill-bg);
  }

  [data-theme="light"] .nav-circle-active {
    background-color: var(--navbar-pill-active-bg);
  }

  [data-theme="light"] .nav-icon-active {
    color: var(--text-primary);
  }

  [data-theme="light"] .nav-icon-inactive {
    color: var(--text-tertiary);
  }

  [data-theme="light"] .nav-label-active {
    color: var(--text-primary);
  }

  [data-theme="light"] .nav-label {
    color: var(--text-tertiary);
  }

  [data-theme="daylight"] .bottom-search-input {
    background-color: #ffffff;
    color: #1a1a1a;
    border: 1px solid #e0e0e0;
  }
  [data-theme="daylight"] .bottom-search-input:focus {
    background-color: #ffffff;
    border-color: #f97316;
  }
`}</style>
    </>
  );
};

export default BottomNavbar;